import { PayloadAction } from '@reduxjs/toolkit'
import { CompanyTypes, IActionCompanyRequest } from './types'
import { all, apply, put, takeLatest } from 'redux-saga/effects'
import { CompanyActions } from './index'
import { CompanyWorklist, MockCompanyConts } from '../../assets/data/mock-data-company'
import companyService from '../../services/companies.service'
import { SnackbarActions, SnackBarMessageType } from '../snackbar'
import Messages from '../../assets/locales/pt/translation.json'
import { CompanyIntegrated } from '../../application/domain/models/entity/company.integrated'
import {VehicleIntegrated} from "../../application/domain/models/entity/vehicle.integrated";
import {Address} from "../../application/domain/models/entity/address";
import {Contact} from "../../application/domain/models/entity/contact";
import {Partner} from "../../application/domain/models/entity/partner";

/**
 * <h5>Generator functions that make calls to the company service to obtain the data.</h5>
 * @see {@link https://developer.mozilla.org/pt-BR/docs/Web/JavaScript/Reference/Statements/function*}
 * @typedef CompanySagas
 * @namespace CompanySagas
 * @category React
 * @subcategory Redux / Sagas
 */

/**
 * Generating function that calls to request companyes.
 * @memberof CompanySagas
 * @alias CompanySagas.request
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IActionCompanyRequest>} action User triggered action data.
 */
export function* request(action: PayloadAction<IActionCompanyRequest>) {
    const { companySuccess, companyFailure } = CompanyActions
    const { openSnackBar } = SnackbarActions
    try {
        const data: Array<any> = yield apply(companyService, companyService.getCompany, [action.payload])
        yield put(companySuccess({
            data
        }))
    } catch (err: any) {
        yield put(companyFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
        }))
    }
}

/**
 * Generating function that calls to describe companys.
 * @memberof CompanySagas
 * @alias CompanySagas.describe
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IActionCompanyRequest>} action User triggered action data.
 */
export function* describe(action: PayloadAction<IActionCompanyRequest>) {
    const { describeSuccess, describeFailure } = CompanyActions
    const { openSnackBar } = SnackbarActions
    const { cnpj } = action.payload
    try {
        const company: Array<CompanyIntegrated> = yield apply(companyService, companyService.getCompanyByDocument, [cnpj])
        const companyData: Array<any> = []
        const companyAddress: Array<any> = []
        const companyContacts: Array<any> = []
        const companyVehicles: Array<any> = []
        const companyPartners: Array<any> = []

        for (const data of company) {
            if (data) {
                companyData.push({
                    name: data.corporate_data?.name,
                    cnpj: data.corporate_data?.cnpj,
                    fantasy_name: data.corporate_data?.fantasy_name || 'Não informado.',
                    start_date: data.corporate_data?.start_date || 'Não informado.',
                    state_registration: data.corporate_data?.state_registration || 'Não informado.',
                    establishment_type: data.corporate_data?.establishment_type || 'Não informado.',
                    type: data.corporate_data?.type || 'Não informado.',
                    social_capital: data.corporate_data?.social_capital || 'Não informado.',
                    municipality: data.corporate_data?.municipality || 'Não informado.',
                    uf: data.corporate_data?.uf || 'Não informado.',
                    updated_at: data.corporate_data?.updated_at || 'Não informado.',
                    origin: data.origin
                })
                if (data.vehicles?.length) {
                    companyVehicles.push(...data.vehicles.map((vehicle: VehicleIntegrated) => ({
                        origin: data.origin,
                        ...vehicle
                    })))
                }

                if (data.addresses?.length) {
                    companyAddress.push(...data.addresses.map((address: Address) => ({
                        origin: data.origin,
                        ...address
                    })))
                }
                if (data.contacts?.length) {
                    // eslint-disable-next-line
                    companyContacts.push(...data.contacts.map((contact: Contact) => {
                        if (contact.value) {
                            return {
                                origin: data.origin,
                                ...contact,
                            }
                        }
                    }).filter(Boolean))
                }
                if (data.partners?.length) {
                    companyPartners.push(...data.partners.map((partner: Partner) => ({
                        origin: data.origin,
                        ...partner
                    })))
                }
            }
        }

        yield put(describeSuccess({
            data: {
                cnpj,
                razaoSocial: company.find(o => o.corporate_data)?.corporate_data?.name || '',
                companyDescribe: companyData,
                companyConts: companyContacts,
                companyAddress: companyAddress,
                companyOwner: companyPartners,
                companyAutos: companyVehicles,
                companyWorklist: CompanyWorklist,
                companyCounters: MockCompanyConts
            }
        }))
    } catch (err: any) {
        yield put(describeFailure({
            data: {
                cnpj,
                razaoSocial: 'Não Encontrado.'
            }
        }))
        if (err?.response?.status >= 400 && err?.response?.status <= 499) {
            yield put(openSnackBar({
                title: 'Registro não encontrado.',
                type: SnackBarMessageType.WARNING,
                message: `${Messages.REQUEST.COMPANY.DESCRIBE.INVALID}`
            }))
        } else if (err?.response?.status >= 500 && err?.response?.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.COMPANY.REQUEST.INTERNAL}`
            }))
        }
    }

}

const companySaga = function* () {
    yield all([
        takeLatest(CompanyTypes.COMPANY_REQUEST, request),
        takeLatest(CompanyTypes.DESCRIBE_REQUEST, describe),
    ])
}

export default companySaga
